// /tests/:id/print — the printable paper (Module P2, paper-faithful redesign in P2b). Student copy
// first, a hard page break, then the answer key + a TOS summary for the Lead's file. @media print
// only — no PDF library; the browser's own print dialog produces the paper exactly as previewed
// here. Owner or Lead may open it (same tier as the editor's GET); a Lead printing someone else's
// paper only ever reads it.

import { api } from "../api";
import { printButton, type PrintAudience } from '../print-controls';
import { esc } from "../dom";
import { blockDef, type EditorBlock, type BlockType } from "../blocks";
import { summarize } from "../assessment-tos";
import { layoutBlocks } from "../section-layout";
import { itemBankProvenanceForBlock, blocksForItemCount, type ItemBankBlockProvenance } from "../itembank";
import { FONT_EN_OPTIONS, FONT_KM_OPTIONS, fontStack, DEFAULT_FONT_EN, DEFAULT_FONT_KM, PAPER_KIND_LABEL, isPaperKind } from "../print-fonts";

interface PrintResponse {
  assessment: { title: string; kind: "test" | "worksheet"; grade: number | null; subject: string; test_date: string | null; time_min: number | null };
  header: {
    school_name_en: string; school_name_km: string; logo_data_url: string; class_label: string;
    teacher_name: string;
    paper_kind: string; month_label: string; font_en: string; font_km: string;
  };
  blocks: Array<{ id: string; type: BlockType; marks: number; bloom: "k" | "c" | "a" | null; difficulty: "E" | "M" | "D" | null; content: Record<string, unknown> }>;
  total_marks: number;
}

const BLOOM_LABEL: Record<string, string> = { k: "K", c: "C", a: "A" };

function studentAssetHtml(provenance: ItemBankBlockProvenance | null): string {
  if (!provenance) return "";
  return provenance.assets
    .filter((asset) => asset.exists && ((asset.type === "visual" && asset.data_url?.startsWith("data:image/")) || (asset.type === "audio" && asset.data_url?.startsWith("data:audio/"))))
    .map((asset, index) => asset.type === "visual"
      ? `<img class="ib-student-visual" src="${esc(asset.data_url!)}" alt="Visual ${index + 1} for ${esc(provenance.card_id)}" style="display:block;max-width:100%;max-height:280px;object-fit:contain;margin:0 auto 10px" />`
      : `<div class="ib-student-audio no-print"><p class="hint">Audio stimulus for ${esc(provenance.card_id)}</p><audio controls preload="metadata" src="${esc(asset.data_url!)}"></audio></div>`)
    .join("");
}

function teacherAssetHtml(provenance: ItemBankBlockProvenance | null): string {
  if (!provenance) return "";
  return provenance.assets
    .filter((asset) => asset.type === "audio" && asset.exists && asset.data_url?.startsWith("data:audio/"))
    .map((asset) => `<div class="ib-teacher-audio no-print"><p class="hint">Playable audio stimulus</p><audio controls preload="metadata" src="${esc(asset.data_url!)}"></audio></div>`)
    .join("");
}

function provenanceHtml(provenance: ItemBankBlockProvenance | null): string {
  if (!provenance) return "";
  const taughtNodes = Object.values(provenance.taught.by_grade)
    .flatMap((grade) => grade.required_nodes ?? [])
    .filter((node, index, all) => all.indexOf(node) === index);
  const assets = provenance.assets
    .map((asset) => `${asset.type}: ${asset.path} · ${asset.sha256?.slice(0, 12) ?? "no hash"}`)
    .join("; ");
  return `<div class="ib-print-provenance" style="margin-top:8px;padding-top:6px;border-top:1px solid #ccd3db;font-size:11px;line-height:1.45;overflow-wrap:anywhere">
    <strong>Item Bank ${esc(provenance.card_id)}</strong> · version ${esc(provenance.card_version_sha256)}<br />
    ${esc(provenance.band)} · ${esc(provenance.component)} · ${esc(provenance.skill_task_family)} · ${provenance.items} item${provenance.items === 1 ? "" : "s"} · ${provenance.marks} mark${provenance.marks === 1 ? "" : "s"} · ${esc(provenance.difficulty)} · licence ${esc(provenance.licence_tier)} · ${esc(provenance.printable_mode)}<br />
    Taught bounds: ${taughtNodes.length ? taughtNodes.map(esc).join(", ") : "none"}<br />
    Assets: ${esc(assets)}
  </div>`;
}

function paperTitle(a: PrintResponse["assessment"], h: PrintResponse["header"]): string {
  const kindLabel = isPaperKind(h.paper_kind) ? PAPER_KIND_LABEL[h.paper_kind] : esc(a.title);
  return h.month_label ? `${kindLabel} (${esc(h.month_label)})` : kindLabel;
}

function paperHeader(a: PrintResponse["assessment"], h: PrintResponse["header"], totalMarks: number): string {
  const logo = h.logo_data_url ? `<img src="${esc(h.logo_data_url)}" class="tp-logo" />` : "";
  return `<div class="tp-header">
    ${logo}
    ${h.school_name_km ? `<div class="tp-schoolname-km" lang="km">${esc(h.school_name_km)}</div>` : ""}
    ${h.school_name_en ? `<div class="tp-schoolname-en">${esc(h.school_name_en)}</div>` : ""}
    <h1 class="tp-title">${paperTitle(a, h)}</h1>
    <table class="tp-info-grid">
      <colgroup><col class="tp-col-name" /><col class="tp-col-sex" /><col class="tp-col-date" /></colgroup>
      <tbody>
        <tr>
          <td>Name: __________________</td>
          <td>Sex: Female&nbsp;☐&nbsp;&nbsp;Male&nbsp;☐</td>
          <td>Date: ${a.test_date ? esc(a.test_date) : "……………"}</td>
        </tr>
        <tr>
          <td colspan="2">Subject: ${esc(a.subject) || "English"}</td>
          <td>Duration: ${a.time_min ? `${a.time_min} minutes` : "………"}</td>
        </tr>
        <tr>
          <td colspan="2">Grade: ${a.grade ?? "………"}${h.class_label ? ` &nbsp;·&nbsp; Class: ${esc(h.class_label)}` : ""}</td>
          <td>Mark: ………/${totalMarks}</td>
        </tr>
      </tbody>
    </table>
    <p class="tp-signature">Parent's Signature<span class="tp-signature-rule"></span></p>
  </div>`;
}

export async function renderTestPrint(app: HTMLElement, params: Record<string, string>, query:URLSearchParams=new URLSearchParams()): Promise<void> {
  await import("../styles/fonts.css"); // /tests/* only — never in the app-shell bundle
  const id = params.id!;
  const audience:PrintAudience=query.get('audience')==='key'?'key':'student';
  const data = await api<PrintResponse>(`/api/assessments/${encodeURIComponent(id)}`);
  const blocks: EditorBlock[] = data.blocks.map((b) => ({ id: b.id, type: b.type, marks: b.marks, bloom: b.bloom, difficulty: b.difficulty, content: b.content }));
  const s = summarize(blocksForItemCount(blocks), { grade: data.assessment.grade, kind: data.assessment.kind });
  const layout = layoutBlocks(blocks);

  const fontEnStack = fontStack(FONT_EN_OPTIONS, data.header.font_en, DEFAULT_FONT_EN);
  const fontKmStack = fontStack(FONT_KM_OPTIONS, data.header.font_km, DEFAULT_FONT_KM);
  const pageStyle = `--tp-font-en: ${fontEnStack}; --tp-font-km: ${fontKmStack};`;

  const renderBlock = (b: EditorBlock, i: number): string => {
    const lay = layout[i]!;
    if (b.type === "section") {
      return `<div class="tp-section tp-section-level${lay.sectionLevel}">${esc(lay.sectionLabel ?? "")} ${esc(String((b.content as { title?: string }).title ?? ""))} (${lay.sectionMarks ?? 0} mark${lay.sectionMarks === 1 ? "" : "s"})</div>`;
    }
    const qno = lay.questionNo ? `<span class="tp-qno">${lay.questionNo}.</span>` : "";
    const provenance = itemBankProvenanceForBlock(b);
    return `<div class="tp-block"${provenance ? ` style="white-space:pre-line"` : ""}>${studentAssetHtml(provenance)}${qno}${blockDef(b.type).renderStudent(b.content)}</div>`;
  };

  const studentItems = blocks.map(renderBlock).join("");

  const keyItems = blocks
    .map((b, i) => {
      if (b.type === "section") return "";
      const key = blockDef(b.type).renderKey(b.content);
      if (!key) return "";
      const lay = layout[i]!;
      const tags = `${b.marks} mark${b.marks === 1 ? "" : "s"}${b.bloom ? ` · ${BLOOM_LABEL[b.bloom]}` : ""}${b.difficulty ? ` · ${b.difficulty}` : ""}`;
      const provenance = itemBankProvenanceForBlock(b);
      return `<div class="tp-key-row">${lay.questionNo ? `<strong>${lay.questionNo}.</strong> ` : ""}${key} <span class="muted tp-key-tags">(${tags})</span>${teacherAssetHtml(provenance)}${provenanceHtml(provenance)}</div>`;
    })
    .join("");

  const tosTable = s.bloom
    ? `<table class="tp-tos-table">
        <thead><tr><th></th><th>K</th><th>C</th><th>A</th></tr></thead>
        <tbody><tr><td>Actual</td><td>${Math.round(s.bloom.k * 100)}%</td><td>${Math.round(s.bloom.c * 100)}%</td><td>${Math.round(s.bloom.a * 100)}%</td></tr>
        <tr><td>Target</td><td>50%</td><td>30%</td><td>20%</td></tr></tbody>
      </table>`
    : "";

  app.innerHTML = `
    <div class="topbar no-print">
      <a href="javascript:history.back()" class="pill-btn">← Back</a>
      ${printButton(audience)}
      <a data-link href="/tests/${esc(id)}/print?audience=${audience==='student'?'key':'student'}">${audience==='student'?'Open separate teacher key':'Open student copy'}</a>
    </div>
    <p class="hint no-print tp-mobile-hint">For the cleanest layout, print from a computer — this still previews fine here.</p>

    ${audience==='student'?`<div class="tp-page" style="${esc(pageStyle)}">
      ${paperHeader(data.assessment, data.header, data.total_marks)}
      <div class="tp-body">${studentItems || `<p class="hint">No blocks yet.</p>`}</div>
    </div>`:''}

    ${audience==='key'?`<div class="tp-page tp-key-page" style="${esc(pageStyle)}">
      ${paperHeader(data.assessment, data.header, data.total_marks)}
      <p class="tp-key-banner">ANSWER KEY — TEACHER COPY. Do not distribute to students.</p>
      <div class="tp-body">${keyItems || `<p class="hint">No answer keys to show (open-response blocks only).</p>`}</div>
      <h2>Table of Specifications</h2>
      <p class="muted">${data.total_marks} marks · ${s.itemCount} item${s.itemCount === 1 ? "" : "s"}</p>
      ${tosTable}
    </div>`:''}`;

  app.querySelector<HTMLButtonElement>("#print")!.addEventListener("click", () => window.print());
}
